import React, { useState, useEffect } from "react";
import {
  Container,
  Form,
  FormGroup,
  Input,
  Button,
  InputGroupText,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import { Link, useNavigate } from "react-router-dom";
import { Formik, Field } from "formik";
import * as Yup from "yup";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import "./Login.css"
import apiRequest from '../../../api';

const Login = () => {
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();

  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    if (!localStorage.getItem('Language'))
      localStorage.setItem('Language', 'EN');
    return localStorage.getItem('Language');
  });
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);

  const toggleLanguageDropdown = () => setLanguageDropdownOpen(!languageDropdownOpen);
  useEffect(() => {
    localStorage.setItem('Language', selectedLanguage);
  }, [selectedLanguage]);

  const togglePasswordVisibility = () => setPasswordVisible(!passwordVisible);

  const validationSchema = Yup.object({
    username: Yup.string().required("Username is required"),
    password: Yup.string().required("Password is required"),
  });

  const handleSubmit = async (values) => {
    try {
      const response = await apiRequest('/api/users/login', 'POST', {
        username: values.username,
        password: values.password,
      });
      const { jwt, info, language } = response;
      sessionStorage.setItem("jwt", jwt);
      sessionStorage.setItem("username", info.username);
      sessionStorage.setItem("email", info.email);
      sessionStorage.setItem("language", language);

      setTimeout(() => {
        // Check if there's a saved intended URL to redirect to
        const intendedUrl = sessionStorage.getItem("intendedUrl");
        
        if (intendedUrl) {
          // Clear the intended URL
          sessionStorage.removeItem("intendedUrl");
          
          // Validate if user has permission to access the intended URL
          const isAdminRoute = intendedUrl.startsWith('/admin/');
          const userRole = info.role;
          
          if (isAdminRoute && userRole !== "MANAGER") {
            // User tried to access admin route but doesn't have permission
            // Redirect to their default route instead
            navigate("/home");
          } else if (!isAdminRoute && userRole === "MANAGER") {
            // Manager tried to access user route, redirect to admin default
            navigate("/admin/timetable");
          } else {
            // User has permission, redirect to intended URL
            navigate(intendedUrl);
          }
        } else {
          // Default behavior based on user role
          if (info.role === "USER") {
            navigate("/home");
          } else if (info.role === "MANAGER") {
            navigate("/admin/timetable");
          }
        }
      }, 100);

    } catch (error) {
      console.log(error);
      if (selectedLanguage === 'EN')
        setErrorMessage("Invalid username or password");
      else
        setErrorMessage("Uneti podaci nisu validni");
    }
  };

  return (
    <div className="login-page">
      <div className="login-overlay">
        <div className="login-card">
          <div className="login-logo">
            <img
              src="/assets/img/FORUM26_white.png"
              alt="Logo"
              className="logo-image"
            />
          </div>

          <Formik
            initialValues={{
              username: "",
              password: "",
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({ handleSubmit }) => (
              <Form onSubmit={handleSubmit} className="login-form">
                <FormGroup className="form-group">
                  <div className="input-wrapper" style={{borderRadius: "15px"}}>
                    <img
                      src="/assets/icons/username.png"
                      alt="username"
                      className="input-icon"
                    />
                    <Field
                      as={Input}
                      type="text"
                      name="username"
                      placeholder={selectedLanguage === "EN" ? "Username" : "Korsničko ime"}
                      className="form-control"
                    />
                  </div>
                </FormGroup>

                <FormGroup className="form-group">
                  <div className="input-wrapper">
                    <img
                      src="/assets/icons/lock.svg"
                      alt="password"
                      className="input-icon"
                    />
                    <Field
                      as={Input}
                      type={passwordVisible ? "text" : "password"}
                      name="password"
                      placeholder={selectedLanguage === "EN" ? "Password" : "Šifra"}
                      className="form-control"
                    />
                    <div className="password-toggle" onClick={togglePasswordVisibility}>
                      {passwordVisible ? <FaEyeSlash /> : <FaEye />}
                    </div>
                  </div>
                </FormGroup>

                {errorMessage && (
                  <div className="error-message">{errorMessage}</div>
                )}

                <Button color="primary" type="submit" className="login-button">
                  {selectedLanguage === "EN" ? "Login" : "Uloguj se"}
                </Button>

                <div className="forgot-password">
                  <Link to="/forgot" className="forgot-link">
                    {selectedLanguage === "EN" ? "Forgot password?" : "Zaboravili ste šifru?"}
                  </Link>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>

      <Dropdown
        isOpen={languageDropdownOpen}
        toggle={toggleLanguageDropdown}
        style={{
          position: "fixed",
          bottom: "10px",
          right: "10px",
          zindex: "1000"
        }}
      >
        <DropdownToggle caret className="lang-toggle">
          {selectedLanguage}
        </DropdownToggle>
        <DropdownMenu>
          <DropdownItem onClick={() => setSelectedLanguage('EN')}>EN</DropdownItem>
          <DropdownItem onClick={() => setSelectedLanguage('RS')}>RS</DropdownItem>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

export default Login;